import React, { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
// import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Brand from '../../../layout/Brand/Brand';
import Navigation, { NavigationLine } from '../../../layout/Navigation/Navigation';
import User from '../../../layout/User/User';
import Button from '../../../components/bootstrap/Button';
import Icon from '../../../components/icon/Icon';
import {
	// componentPagesMenu,
	// dashboardPagesMenu,
	// demoPagesMenu,
	// gettingStartedPagesMenu,
	// pageLayoutTypesPagesMenu,
	companyPagesMenu,
	talentPagesMenu,
	settingsMenu,
	talentSettingsMenu,
} from '../../../menu';
import ThemeContext from '../../../contexts/themeContext';
// import Card, { CardBody } from '../../../components/bootstrap/Card';
// import Hand from '../../../assets/img/hand.png';
// import HandWebp from '../../../assets/img/hand.webp';
// import Icon from '../../../components/icon/Icon';
// import Button from '../../../components/bootstrap/Button';
// import useDarkMode from '../../../hooks/useDarkMode';
import Aside, { AsideBody, AsideFoot, AsideHead } from '../../../layout/Aside/Aside';
import AuthContext from '../../../contexts/authContext';
import { UserType } from '../../../common/data/dummyRolesData';
import { useOrganization } from '../../../contexts/organizationContext';
import { usePermissions } from '../../../contexts/permissionContext';
import { canShowMenuItem } from '../../../common/constants/menuPermissionMapping';
import { PermissionCode } from '../../../common/constants/permissionCodes';
import { useEntitlements } from '../../../hooks/useEntitlements';
import {
	checkOrgBillingComplete,
	useTalentBillingCompleteness,
} from '../../../hooks/useBillingCompleteness';

/** @userstory US-MENU-01, US-MENU-02, US-MENU-04, US-MENU-05, US-MENU-06, US-MENU-08, US-MENU-09 */
import useTalentEmploymentTypes from '../../../hooks/useTalentEmploymentTypes';

// Animation variants for Apple-style menu transitions
const menuTransitionVariants = {
	initial: (direction: number) => ({
		opacity: 0,
		x: direction > 0 ? 20 : -20,
	}),
	animate: {
		opacity: 1,
		x: 0,
		transition: {
			duration: 0.2,
			ease: 'easeInOut',
		},
	},
	exit: (direction: number) => ({
		opacity: 0,
		x: direction > 0 ? -20 : 20,
		transition: {
			duration: 0.15,
			ease: 'easeInOut',
		},
	}),
};

// Back button hover animation
const backButtonVariants = {
	initial: { x: 0 },
	hover: { x: -4 },
	tap: { scale: 0.96 },
};

/**
 * Filter menu items based on user permissions
 * @param menu The menu object to filter
 * @param isAdmin Whether the user is an Admin
 * @param permissions The user's permissions array
 */
const filterMenuByPermissions = (
	menu: Record<string, any>,
	isAdmin: boolean,
	permissions: PermissionCode[],
	hasAddOn: (key: string) => boolean,
): Record<string, any> => {
	const filtered: Record<string, any> = {};

	Object.entries(menu).forEach(([key, item]) => {
		if (!item || typeof item !== 'object') return;
		if (item.hideFromOperationalMenu) return;
		if (item.addOnKey && !hasAddOn(item.addOnKey)) return;

		const menuId = item.id?.toLowerCase() || key.toLowerCase();

		// Check if this menu item should be visible
		if (!canShowMenuItem(menuId, isAdmin, permissions)) {
			return;
		}

		// If there's a subMenu, filter it recursively
		if (item.subMenu && typeof item.subMenu === 'object') {
			const filteredSubMenu = filterMenuByPermissions(item.subMenu, isAdmin, permissions, hasAddOn);
			// Only include parent if at least one subMenu item is visible
			if (Object.keys(filteredSubMenu).length > 0) {
				filtered[key] = { ...item, subMenu: filteredSubMenu };
			}
		} else {
			filtered[key] = item;
		}
	});

	return filtered;
};

/**
 * Get operational menu (excluding Settings which is rendered separately)
 */
const getOperationalMenu = (
	menu: Record<string, any>,
	isAdmin: boolean,
	permissions: PermissionCode[],
	hasAddOn: (key: string) => boolean,
): Record<string, any> => {
	const filtered = filterMenuByPermissions(menu, isAdmin, permissions, hasAddOn);
	// Remove Settings from operational menu (rendered separately at bottom)
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { Settings, ...operationalMenu } = filtered;
	return operationalMenu;
};

const talentFreelanceOnlyMenuKeys = new Set(['quotes', 'projects', 'timesheets', 'invoices']);

const applyTalentEmploymentMenuVisibility = (
	menu: Record<string, any>,
	isEmployeeOnly: boolean,
): Record<string, any> => {
	if (!isEmployeeOnly) return menu;

	return Object.fromEntries(
		Object.entries(menu).flatMap(([key, item]) => {
			if (key !== 'FreelanceManagement' || !item?.subMenu) return [[key, item]];

			const subMenu = Object.fromEntries(
				Object.entries(item.subMenu).filter(
					([subKey]) => !talentFreelanceOnlyMenuKeys.has(subKey),
				),
			);
			const firstSubItem = Object.values(subMenu)[0] as { path?: string } | undefined;

			if (!firstSubItem) return [];

			return [[key, { ...item, path: firstSubItem.path ?? item.path, subMenu }]];
		}),
	);
};

const DefaultAside = () => {
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);
	const { userData } = useContext(AuthContext);
	const { selectedOrganization } = useOrganization();
	const { isAdmin, permissions } = usePermissions();
	const { hasAddOn } = useEntitlements();
	const location = useLocation();
	const navigate = useNavigate();

	// Check if we're in the settings area
	const isInSettingsArea = location.pathname.startsWith('/company/settings');
	const isTalentInSettingsArea = location.pathname.startsWith('/talent/settings');

	const { t } = useTranslation('menu');
	const isTalent = userData?.role === UserType.FREELANCE;
	const { isEmployeeOnly } = useTalentEmploymentTypes(isTalent);

	// const { darkModeStatus } = useDarkMode();

	// Operational IA renders the grouped funnel (Sourcing → Talent Pool → Relazioni & Trust →
	// Intelligence) as collapsible sections (US-C-MENU-11). Each group is its own navigable parent;
	// getOperationalMenu permission-filters and drops empty groups.
	const operationalMenu = useMemo(
		() => getOperationalMenu(companyPagesMenu, isAdmin, permissions, hasAddOn),
		[isAdmin, permissions, hasAddOn],
	);

	// Settings entry (only visible to Admin)
	const settingsEntry = useMemo(() => {
		if (!isAdmin) return null;
		return { Settings: companyPagesMenu.Settings };
	}, [isAdmin]);

	// Get talent operational menu (without Settings)
	const talentOperationalMenu = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { Settings, ...operational } = talentPagesMenu;
		const filteredMenu = Object.fromEntries(
			Object.entries(operational).filter(([, item]) => {
				if (!item || typeof item !== 'object') return false;
				const menuItem = item as Record<string, any>;
				if (menuItem.hideFromOperationalMenu) return false;
				if (menuItem.addOnKey && !hasAddOn(menuItem.addOnKey)) return false;
				return true;
			}),
		);
		return applyTalentEmploymentMenuVisibility(filteredMenu, isEmployeeOnly);
	}, [hasAddOn, isEmployeeOnly]);

	// Talent settings entry
	const talentSettingsEntry = useMemo(() => {
		return { Settings: talentPagesMenu.Settings };
	}, []);

	// --- Billing gating: disable subscription menu until billing data is complete ---
	// @userstory US-STRIPE-07
	const orgBillingComplete = useMemo(
		() => checkOrgBillingComplete(selectedOrganization),
		[selectedOrganization],
	);
	const gatedSettingsMenu = useMemo(() => {
		const addOnFilteredSettingsMenu = Object.fromEntries(
			Object.entries(settingsMenu).filter(([, item]) => {
				const menuItem = item as Record<string, any>;
				return !menuItem.addOnKey || hasAddOn(menuItem.addOnKey);
			}),
		);

		if (orgBillingComplete) return addOnFilteredSettingsMenu;
		return {
			...addOnFilteredSettingsMenu,
			subscription: {
				...settingsMenu.subscription,
				disabled: true,
				disabledTooltip: t('billingRequired.tooltip', { ns: 'subscription' }),
			},
		};
	}, [hasAddOn, orgBillingComplete, t]);

	// @userstory US-STRIPE-08
	const { isBillingComplete: talentBillingComplete } = useTalentBillingCompleteness(
		isTalent && isTalentInSettingsArea,
	);
	const gatedTalentSettingsMenu = useMemo(() => {
		if (!isTalent || talentBillingComplete) return talentSettingsMenu;
		return {
			...talentSettingsMenu,
			subscription: {
				...talentSettingsMenu.subscription,
				disabled: true,
				disabledTooltip: t('billingRequired.tooltip', { ns: 'subscription' }),
			},
		};
	}, [isTalent, talentBillingComplete, t]);

	return (
		<Aside>
			<AsideHead>
				<Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
			</AsideHead>
			<AsideBody>
				{/* {!doc && (
					<>
						<Navigation menu={dashboardPagesMenu} id='aside-dashboard' />
						<NavigationLine />
						<Navigation menu={demoPagesMenu} id='aside-demo-pages' />
						<NavigationLine />
						<Navigation menu={pageLayoutTypesPagesMenu} id='aside-menu' />
					</>
				)} */}

				{/* {doc && (
					<>
						<Navigation menu={gettingStartedPagesMenu} id='aside-docMenu' />
						<NavigationLine />
						<Navigation menu={componentPagesMenu} id='aside-componentsMenu' />
						<NavigationLine />
					</>
				)} */}

				{/* {asideStatus && doc && (
					<Card className='m-3 '>
						<CardBody className='pt-0'>
							<img srcSet={HandWebp} src={Hand} alt='Hand' width={130} height={130} />
							<p
								className={classNames('h4', {
									'text-dark': !darkModeStatus,
									'text-light': darkModeStatus,
								})}>
								{t('Everything is ready!') as ReactNode}
							</p>
							<Button
								color='info'
								isLight
								className='w-100'
								onClick={() => {
									localStorage.setItem('facit_asideDocStatus', 'false');
									setDoc(false);
								}}>
								{t('Demo Pages') as ReactNode}
							</Button>
						</CardBody>
					</Card>
				)} */}

				{/* Company Route */}
				{userData.role === UserType.COMPANY && (
					<AnimatePresence mode='wait' custom={isInSettingsArea ? 1 : -1}>
						{isInSettingsArea ? (
							// Settings Area Menu with Apple-style transition
							<motion.div
								key='settings-menu'
								custom={1}
								variants={menuTransitionVariants}
								initial='initial'
								animate='animate'
								exit='exit'>
								{/* Back to Dashboard button with micro-interaction */}
								<motion.div
									className='px-3 py-2'
									variants={backButtonVariants}
									initial='initial'
									whileHover='hover'
									whileTap='tap'
									transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
									<Button
										id='company-settings-back-to-dashboard'
										color='link'
										className='settings-back-button p-0 text-decoration-none w-100 text-start'
										onClick={() => navigate('/company/dashboard')}>
										<Icon icon='ArrowBack' className='back-icon' />
										<span className='back-text'>{t('Back to Dashboard')}</span>
									</Button>
								</motion.div>
								<NavigationLine />
								{/* Settings Menu */}
							<Navigation menu={gatedSettingsMenu} id='settings-menu' />
							</motion.div>
						) : (
							// Operational Area Menu with Apple-style transition
							<motion.div
								key='operational-menu'
								custom={-1}
								variants={menuTransitionVariants}
								initial='initial'
								animate='animate'
								exit='exit'>
								{/* Operational Menu */}
								<Navigation menu={operationalMenu} id='company-dashboard' />
								<NavigationLine />

								{/* Settings Entry (Admin only) */}
								{settingsEntry && (
									<Navigation menu={settingsEntry} id='company-settings' />
								)}
							</motion.div>
						)}
					</AnimatePresence>
				)}

				{/* Talent Route */}
				{userData.role === UserType.FREELANCE && (
					<AnimatePresence mode='wait' custom={isTalentInSettingsArea ? 1 : -1}>
						{isTalentInSettingsArea ? (
							// Settings Area Menu with Apple-style transition
							<motion.div
								key='talent-settings-menu'
								custom={1}
								variants={menuTransitionVariants}
								initial='initial'
								animate='animate'
								exit='exit'>
								{/* Back to Dashboard button with micro-interaction */}
								<motion.div
									className='px-3 py-2'
									variants={backButtonVariants}
									initial='initial'
									whileHover='hover'
									whileTap='tap'
									transition={{ type: 'spring', stiffness: 400, damping: 25 }}>
									<Button
										id='talent-settings-back-to-dashboard'
										color='link'
										className='settings-back-button p-0 text-decoration-none w-100 text-start'
										onClick={() => navigate('/talent/dashboard')}>
										<Icon icon='ArrowBack' className='back-icon' />
										<span className='back-text'>{t('Back to Dashboard')}</span>
									</Button>
								</motion.div>
								<NavigationLine />
								{/* Talent Settings Menu */}
							<Navigation menu={gatedTalentSettingsMenu} id='talent-settings-menu' />
							</motion.div>
						) : (
							// Operational Area Menu with Apple-style transition
							<motion.div
								key='talent-operational-menu'
								custom={-1}
								variants={menuTransitionVariants}
								initial='initial'
								animate='animate'
								exit='exit'>
								{/* Operational Menu */}
								<Navigation menu={talentOperationalMenu} id='talent-dashboard' />
								<NavigationLine />

								{/* Settings Entry */}
								<Navigation menu={talentSettingsEntry} id='talent-settings-entry' />
							</motion.div>
						)}
					</AnimatePresence>
				)}
			</AsideBody>
			<AsideFoot>
				{/* <nav aria-label='aside-bottom-menu'>
					<div className='navigation'>
						<div
							role='presentation'
							className='navigation-item cursor-pointer'
							onClick={() => {
								localStorage.setItem('facit_asideDocStatus', String(!doc));
								setDoc(!doc);
							}}
							data-tour='documentation'>
							<span className='navigation-link navigation-link-pill'>
								<span className='navigation-link-info'>
									<Icon
										icon={doc ? 'ToggleOn' : 'ToggleOff'}
										color={doc ? 'success' : undefined}
										className='navigation-icon'
									/>
									<span className='navigation-text'>
										{t('menu:Documentation') as ReactNode}
									</span>
								</span>
								<span className='navigation-link-extra'>
									<Icon
										icon='Circle'
										className={classNames(
											'navigation-notification',
											'text-success',
											'animate__animated animate__heartBeat animate__infinite animate__slower',
										)}
									/>
								</span>
							</span>
						</div>
					</div>
				</nav> */}
				<User />
			</AsideFoot>
		</Aside>
	);
};

export default DefaultAside;
